// Web Database Adapter - NO usa expo-sqlite. Persistencia ligera opcional en localStorage

interface RunResult {
  insertId?: number
  rowsAffected?: number
  rows?: any[]
}

class DatabaseAdapterWeb {
  private dbName: string
  private isInitialized: boolean
  // Representamos tablas como Map<string, Record<string, any>> simplificado
  private tables: Map<string, Map<string, any>>

  constructor(dbName = 'myDB') {
    this.dbName = dbName
    this.isInitialized = false
    this.tables = new Map()
  }

  async initialize() {
    if (this.isInitialized) return this
    // Intentar cargar snapshot
    try {
      const raw = globalThis?.localStorage?.getItem(`${this.dbName}_snapshot`)
      if (raw) {
        try {
          const parsed = JSON.parse(raw)
          Object.entries(parsed).forEach(([table, rows]) => {
            const map = new Map<string, any>()
            Object.entries(rows as Record<string, any>).forEach(([k, v]) => map.set(k, v))
            this.tables.set(table, map)
          })
        } catch (e) {
          console.warn('[DatabaseAdapter.web] invalid snapshot JSON, clearing:', (e as any)?.message)
          try { globalThis?.localStorage?.removeItem(`${this.dbName}_snapshot`) } catch {}
        }
      }
    } catch {}
    this.isInitialized = true
    return this
  }

  private persist() {
    try {
      const obj: Record<string, Record<string, any>> = {}
      this.tables.forEach((map, table) => {
        obj[table] = {}
        map.forEach((v, k) => { obj[table][k] = v })
      })
      globalThis?.localStorage?.setItem(`${this.dbName}_snapshot`, JSON.stringify(obj))
    } catch {}
  }

  // Simplificación: parsea algunos CREATE TABLE para registrar nombre de tabla
  async execAsync(sql: string, _params: any[] = []): Promise<RunResult> {
    if (!this.isInitialized) await this.initialize()
    const normalized = sql.trim().toUpperCase()
    if (normalized.startsWith('CREATE TABLE IF NOT EXISTS')) {
      const match = /CREATE TABLE IF NOT EXISTS (\w+)/i.exec(sql)
      const tableName = match?.[1]
      if (tableName && !this.tables.has(tableName)) {
        this.tables.set(tableName, new Map())
        this.persist()
      }
      return { rowsAffected: 0 }
    }
    if (normalized.startsWith('INSERT')) {
      // Esperamos formato VALUES (?,?)...' con primer valor como ID (heurística)
      const tableMatch = /INTO (\w+)/i.exec(sql)
      const tableName = tableMatch?.[1]
      if (tableName) {
        if (!this.tables.has(tableName)) this.tables.set(tableName, new Map())
        const table = this.tables.get(tableName)!
        const idParam = _params?.[0] ?? `${Date.now()}-${Math.random()}`
        table.set(String(idParam), { _id: idParam })
        this.persist()
        return { insertId: 0, rowsAffected: 1 }
      }
      return { rowsAffected: 0 }
    }
    // Otros comandos ignorados silenciosamente para no romper flujos
    return { rowsAffected: 0 }
  }

  async getAllAsync(sql: string, params: any[] = []): Promise<any[]> {
    if (!this.isInitialized) await this.initialize()
    const upper = sql.trim().toUpperCase()
    if (upper.startsWith('SELECT')) {
      const tableMatch = /FROM (\w+)/i.exec(sql)
      const tableName = tableMatch?.[1]
      if (tableName && this.tables.has(tableName)) {
        return Array.from(this.tables.get(tableName)!.values())
      }
      return []
    }
    return []
  }

  async getFirstAsync(sql: string, params: any[] = []) {
    const all = await this.getAllAsync(sql, params)
    return all[0] || null
  }

  async transactionAsync(statements: { sql: string; params?: any[] }[]) {
    for (const s of statements) { await this.execAsync(s.sql, s.params || []) }
    return true
  }

  // API de compatibilidad (similar a expo-sqlite) usada por código legacy: db.transaction(cb)
  // Ejecuta de forma secuencial las sentencias recibidas vía executeSql y llama callbacks de éxito/ error.
  // No implementa atomicidad real pero evita que la UI falle en web.
  // Soporta DOS formatos:
  //   1) transaction((tx)=>{ tx.executeSql(...) })           (estilo expo-sqlite)
  //   2) transaction(["tabla"], 'readonly'|'readwrite')    (estilo IndexedDB simplificado) -> devuelve tx
  transaction(arg: any, mode?: string) {
    // Detección formato IndexedDB: primer argumento array o string
    if (Array.isArray(arg) || typeof arg === 'string') {
      const storeNames: string[] = Array.isArray(arg) ? arg : [arg]
      // Creamos un pseudo transaction object
      const tx: any = {
        objectStore: (name: string) => {
          if (!this.tables.has(name)) { this.tables.set(name, new Map()) }
          const table = this.tables.get(name)!

          // Utilidad para emitir request asincrónico con .onsuccess/.onerror asignables
            const makeRequest = (executor: () => any, transform?: (res: any)=>any) => {
              const request: any = { result: undefined, error: undefined, onsuccess: undefined as any, onerror: undefined as any }
              queueMicrotask(() => {
                try {
                  const raw = executor()
                  request.result = transform ? transform(raw) : raw
                  request.onsuccess && request.onsuccess({ target: request })
                } catch (e) {
                  request.error = e
                  if (request.onerror) request.onerror({ target: request })
                }
              })
              return request
            }

          return {
            add: (value: any) => makeRequest(() => { const id = value._id || value.id || `${Date.now()}-${Math.random()}`; table.set(String(id), { ...value, _id: id }); this.persist(); }),
            put: (value: any) => makeRequest(() => { const id = value._id || value.id || `${Date.now()}-${Math.random()}`; table.set(String(id), { ...value, _id: id }); this.persist(); }),
            get: (key: string) => makeRequest(() => table.get(String(key))),
            getAll: () => makeRequest(() => Array.from(table.values())),
            clear: () => makeRequest(() => { table.clear(); this.persist(); }),
          }
        },
      }
      return tx
    }

    // Formato callback estilo sqlite
    const callback = arg
    if (typeof callback !== 'function') { console.error('[WebDB] transaction callback no es función'); return }
    const tx = {
      executeSql: async (sql: string, params: any[] = [], successCb?: Function, errorCb?: Function) => {
        try {
          const result = await this.execAsync(sql, params)
          let rowsArray: any[] = []
          try { if (/^\s*SELECT/i.test(sql)) { rowsArray = await this.getAllAsync(sql, params) } } catch {}
          const rows = { _array: rowsArray, length: rowsArray.length, item: (i: number) => rowsArray[i] }
          successCb && successCb(tx, { rows, insertId: result.insertId ?? null, rowsAffected: result.rowsAffected ?? 0 })
        } catch (e) {
          console.error('[WebDB] executeSql error', e)
          errorCb && errorCb(tx, e)
        }
      },
    }
    try { callback(tx) } catch (e) { console.error('[WebDB] transaction callback error', e) }
  }

  // Compat shim para prepareSync en algunos módulos (devuelve interfaz mínima)
  prepareSync(sql: string) {
    return {
      getAllSync: () => [],
      getFirstSync: () => null,
      executeSync: () => ({ changes: 0, insertId: null }),
      finalizeSync: () => {},
    }
  }

  isReady() { return this.isInitialized }
  async close() { this.isInitialized = false }
}

let instance: DatabaseAdapterWeb | null = null
export const getDatabaseAdapter = (dbName = 'myDB') => { if (!instance) instance = new DatabaseAdapterWeb(dbName); return instance }
export const initializeDatabaseAdapter = async (dbName = 'myDB') => { const a = getDatabaseAdapter(dbName); await a.initialize(); return a }
export default DatabaseAdapterWeb
